/**
 * The confirmation card in the chat (stage 3): everything that will be created and linked, made
 * visible — one row per object with NEU/BESTEHEND (new/existing), the fields with their labels,
 * the edges with their target, the summary below. The text comes from the server, never from the
 * model.
 *
 * `[Ja]` and `[Nein]` go to the approval route (`PortalWriteAPI.decideApproval`), never to the
 * model: after the card the model contributes nothing more; what gets executed is what the user
 * has seen. `[Ändern]` is a message — the assistant then asks what should be different.
 *
 * Two quick clicks are one request: `busy` locks the buttons, and the server rejects a second
 * click anyway (409). An expired card only offers „Neu planen".
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { MarkdownLink } from '../components/markdownLinkComponent';
import { decideApproval, type ApprovalDecision, type ApprovalResult } from '../PortalWriteAPI';
import { requestChatSend } from './chatSend';
import { GuideCollapsed } from './GuideCollapsed';
import { guideLocale, isConfirmBlock, type ConfirmBlockRow, type GuideLocale } from './guideBlocks';

export interface GuideConfirmBlockProps {
    resource: unknown;
    className?: string;
    /** True once a newer message exists: the card folds to one line. */
    collapsed?: boolean;
}

const TEXT: Record<GuideLocale, Record<string, string>> = {
    de: {
        new: 'NEU',
        existing: 'BESTEHEND',
        planned: 'NEU',
        busy: 'Wird angelegt …',
        done: 'Angelegt.',
        cancelled: 'Abgebrochen. Es wurde nichts angelegt.',
        partial: 'Teilweise angelegt — Einzelheiten unten.',
        rejected: 'Diese Karte ist nicht mehr gültig.',
        failed: 'Das hat nicht geklappt.',
        expired: 'Diese Karte ist abgelaufen.',
        created: 'angelegt',
        linked: 'verknüpft',
        skipped: 'übersprungen',
        outcomeFailed: 'fehlgeschlagen',
        replan: 'Neu planen',
        edit: 'Ich möchte an der Karte etwas ändern.',
        validUntil: 'gültig bis',
        openEditor: 'Im Editor öffnen',
    },
    en: {
        new: 'NEW',
        existing: 'EXISTING',
        planned: 'NEW',
        busy: 'Creating …',
        done: 'Created.',
        cancelled: 'Cancelled. Nothing was created.',
        partial: 'Partially created — details below.',
        rejected: 'This card is no longer valid.',
        failed: 'That did not work.',
        expired: 'This card has expired.',
        created: 'created',
        linked: 'linked',
        skipped: 'skipped',
        outcomeFailed: 'failed',
        replan: 'Plan again',
        edit: 'I want to change something on the card.',
        validUntil: 'valid until',
        openEditor: 'Open in editor',
    },
};

const ACTION_VARIANT: Record<'confirm' | 'confirmPublish' | 'edit' | 'cancel', 'default' | 'secondary' | 'outline'> = {
    confirm: 'default',
    confirmPublish: 'default',
    edit: 'secondary',
    cancel: 'outline',
};

function Row({ row, locale }: { row: ConfirmBlockRow; locale: GuideLocale }) {
    const t = TEXT[locale];
    return (
        <div className='flex flex-col gap-0.5 rounded-sm border border-border/60 bg-background px-2.5 py-2' data-testid='guide-confirm-row' data-path={row.path}>
            <div className='flex items-baseline justify-between gap-2'>
                <div className='min-w-0'>
                    <span className='text-xs uppercase tracking-wide text-muted-foreground'>{row.classLabel[locale] || row.classLabel.de}</span>
                    <span className='ml-2 break-words font-medium'>{row.label}</span>
                </div>
                <span className={cn('shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold tracking-wide', row.state === 'new' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground')}>
                    {t[row.state]}
                </span>
            </div>
            {row.fields.length > 0 && (
                <dl className='m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs'>
                    {row.fields.map((field) => (
                        <div key={field.name} className='contents'>
                            <dt className='text-muted-foreground'>{field.label[locale] || field.label.de}</dt>
                            <dd className='m-0 break-words'>{field.value}</dd>
                        </div>
                    ))}
                </dl>
            )}
            {row.references.length > 0 && (
                <ul className='m-0 list-none p-0 text-xs'>
                    {row.references.map((ref) => (
                        <li key={`${ref.name}:${ref.target}`} className='flex items-baseline gap-2'>
                            <span className='text-muted-foreground'>{ref.label[locale] || ref.label.de}:</span>
                            <span className='min-w-0 break-words'>{ref.target}</span>
                            <span className='ml-auto shrink-0 text-[10px] font-semibold tracking-wide text-muted-foreground'>{t[ref.state]}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

export function GuideConfirmBlock({ resource, className, collapsed = false }: GuideConfirmBlockProps) {
    const { i18n } = useTranslation();
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState<ApprovalResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [edited, setEdited] = useState(false);
    const [decided, setDecided] = useState<ApprovalDecision | null>(null);
    if (!isConfirmBlock(resource)) return null;
    const data = resource.data;
    const locale = guideLocale(i18n?.language);
    const t = TEXT[locale];
    const expired = Number.isFinite(Date.parse(data.expiresAt)) && Date.parse(data.expiresAt) < Date.now();
    const settled = !!result || edited;

    const decide = async (decision: ApprovalDecision) => {
        if (busy || settled || expired) return;
        setBusy(true);
        setDecided(decision);
        setError(null);
        try {
            setResult(await decideApproval(data.planId, decision));
        } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setBusy(false);
        }
    };

    const edit = () => {
        if (busy || settled) return;
        const ok = requestChatSend({ text: t.edit, planId: data.planId, key: `${data.planId}:edit` });
        if (ok) setEdited(true);
    };

    const replan = () => {
        requestChatSend({ text: locale === 'de' ? 'Bitte plane den Vorgang neu.' : 'Please plan the request again.', planId: data.planId, key: `${data.planId}:replan` });
    };

    const title = data.title[locale] || data.title.de;
    const summaryLine = data.summary?.[locale] || data.summary?.de || '';
    return (
        <GuideCollapsed collapsed={collapsed} summary={summaryLine ? `${title} — ${summaryLine}` : title}>
        <div
            className={cn('my-2 flex flex-col gap-2 rounded-md border border-border bg-muted/40 p-3', className)}
            data-testid='guide-confirm-block'
            data-plan-id={data.planId}
            role='group'
            aria-label={data.title[locale] || data.title.de}
        >
            <div className='flex items-baseline justify-between gap-2'>
                <div className='text-sm font-semibold'>{data.title[locale] || data.title.de}</div>
                {!settled && !expired && (
                    <div className='text-[11px] text-muted-foreground'>
                        {t.validUntil} {new Date(data.expiresAt).toLocaleTimeString(locale === 'de' ? 'de-DE' : 'en-US', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                )}
            </div>

            <div className='flex flex-col gap-1.5'>
                {data.rows.map((row) => <Row key={row.path} row={row} locale={locale} />)}
            </div>

            <div className='text-xs text-muted-foreground' data-testid='guide-confirm-summary'>{data.summary[locale] || data.summary.de}</div>

            {data.offers.length > 0 && !settled && (
                <ul className='m-0 list-none p-0 text-xs' data-testid='guide-confirm-offers'>
                    {data.offers.map((offer) => (
                        <li key={offer.questionKey} className='flex items-center gap-2'>
                            <span className='min-w-0'>{offer.text[locale] || offer.text.de}</span>
                            <button
                                type='button'
                                disabled={busy}
                                onClick={() => requestChatSend({ text: locale === 'de' ? 'Ja, auswählen' : 'Yes, choose', planId: data.planId, questionKey: offer.questionKey, key: `${data.planId}:${offer.questionKey}:offer` })}
                                className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), 'h-6 px-2 text-xs')}
                            >
                                {locale === 'de' ? 'Auswählen' : 'Choose'}
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {expired && !result && (
                <div className='flex items-center gap-2 text-xs text-muted-foreground' data-testid='guide-confirm-expired'>
                    <span>{t.expired}</span>
                    <button type='button' onClick={replan} className={cn(buttonVariants({ variant: 'secondary', size: 'sm' }))}>{t.replan}</button>
                </div>
            )}

            {!settled && !expired && (
                <div className='flex flex-wrap gap-1.5' data-testid='guide-confirm-actions'>
                    {data.actions.map((action) => {
                        const label = action.label[locale] || action.label.de;
                        // The narrowing of `action.id` does not survive a closure call —
                        // hence the value is captured in a constant up front.
                        const decision = action.id === 'edit' ? null : action.id;
                        const onClick = decision === null ? edit : () => decide(decision);
                        return (
                            <button
                                key={action.id}
                                type='button'
                                disabled={busy}
                                data-action={action.id}
                                onClick={onClick}
                                className={cn(buttonVariants({ variant: ACTION_VARIANT[action.id] ?? 'secondary', size: 'sm' }))}
                            >
                                {busy && action.id === decided && action.id !== 'cancel' ? t.busy : label}
                            </button>
                        );
                    })}
                </div>
            )}

            {error && <div className='text-xs text-destructive' role='alert'>{t.failed} {error}</div>}

            {result && (
                <div className='flex flex-col gap-1 text-xs' data-testid='guide-confirm-result' data-status={result.status} role='status' aria-live='polite'>
                    <div className={cn('font-medium', result.status === 'done' ? 'text-primary' : result.status === 'cancelled' ? 'text-muted-foreground' : 'text-destructive')}>
                        {t[result.status] ?? result.message ?? result.status}
                    </div>
                    {result.results.length > 0 && (
                        <ul className='m-0 list-none p-0'>
                            {result.results.map((step) => (
                                <li key={`${step.path}:${step.outcome}`} className='flex gap-2'>
                                    {/* "Gebäude Scharrnstraße 20 — angelegt", not the raw outcome key (28.09.2026). */}
                                    <span className='min-w-0 break-words'>
                                        {step.label ?? step.className}
                                        {' — '}
                                        {step.outcome === 'failed' ? t.outcomeFailed : (t[step.outcome] ?? step.outcome)}
                                        {step.reason ? ` (${step.reason})` : ''}
                                    </span>
                                    {step.route && step.outcome === 'created' && (
                                        <MarkdownLink href={step.route} className='shrink-0' data-testid='guide-confirm-result-link'>
                                            {t.openEditor}
                                        </MarkdownLink>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
        </GuideCollapsed>
    );
}
